<script setup lang="ts">
import type LxcError from "~/core/models/LxcError.class";
import type { Pagination } from "~/core/models/Pagination.interface";
import type { RelationEntity } from "~/modules/relation/models/RelationEntity.interface";

defineProps<{
  fromTo: "from" | "to";
  pagination?: Pagination;
  relations?: RelationEntity[];
  isLoading?: boolean;
  error?: LxcError | null;
  /**
   * Factory function that creates delete functions for relation entities.
   *
   * This function takes an organization code and returns a parameterless async function
   * that will delete the affiliation with that specific organization. The returned function
   * is then passed to the DeleteAction component which handles the confirmation dialog,
   * loading state, error handling, and success notifications.
   *
   * @param organizationCode - The code of the target organization
   * @returns An async function that performs the deletion when executed
   *
   * @example
   * // Parent component provides the factory
   * <relation-entity-list :delete-fn="getDeleteFn" />
   *
   * // This component calls the factory for each relation
   * const deleteSpecificRelation = deleteFn(123);
   *
   * // DeleteAction component receives and executes the function
   * <delete-action :delete-fn="deleteSpecificRelation" />
   */
  deleteFn?: (organizationCode: number) => () => Promise<void>;
}>();

defineEmits(["changePageAndPageSize", "deleteSuccess"]);
</script>

<template>
  <container-component :error="error" :px="0" :py="0">
    <div v-if="relations && relations.length > 0">
      <div class="relative">
        <div v-if="isLoading" class="absolute inset-0 flex flex-col">
          <div class="flex flex-1 items-center justify-center">
            <lxc-loader :size="20" />
          </div>
        </div>
        <div :class="`flex flex-col gap-2 ${isLoading ? 'opacity-20' : ''}`">
          <relation-entity
            v-for="child in relations"
            :key="child.organizationCode"
            :relation-entity="child"
            :from-to="fromTo"
            :delete-fn="deleteFn"
            @delete-success="$emit('deleteSuccess')"
          />
        </div>
      </div>

      <div class="border-t border-gray-200 mt-8">
        <lxc-pagination
          :context="pagination"
          :px="0"
          :pt="3"
          @change-page-and-page-size="
            (page: number, pageSize: number) =>
              $emit('changePageAndPageSize', page, pageSize)
          "
        />
      </div>
    </div>
  </container-component>
</template>
